/**
 * The frame every screen renders inside.
 *
 * Header, main landmark, footer, and the three things that are easy to leave out of a
 * single-page app and hard to add later: a skip link, a live region that announces route changes,
 * and a persistent search entry point.
 *
 * The header's search box routes to `/search` rather than filtering in place. The in-page quick
 * filter on `/courses` is a different control answering a different question — "narrow this list"
 * versus "answer this question" — and conflating them would make the agentic search look like a
 * slow text filter.
 */
import { Suspense, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';

import { Container, SearchInput, Spinner, Stack } from '@catalog/ui';

import styles from './AppShell.module.css';
import { AppLink } from './AppLink.js';
import { RouteAnnouncer } from './RouteAnnouncer.js';
import { ThemeToggle } from './ThemeToggle.js';
import { UserMenu } from './UserMenu.js';

const NAV = [
  { to: '/courses', label: 'Courses' },
  { to: '/pathways', label: 'Pathways' },
  { to: '/search', label: 'Ask' },
] as const;

export function AppShell() {
  return (
    <div className={styles['shell']}>
      <a className={styles['skipLink']} href="#main">
        Skip to content
      </a>

      <Header />

      <main id="main" className={styles['main']} tabIndex={-1}>
        <Container size="xl">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </Container>
      </main>

      <footer className={styles['footer']}>
        <Container size="xl">
          Pearson video courses licensed to HCLTech. Transcript content is licensed material and is
          available to signed-in users only.
        </Container>
      </footer>

      <RouteAnnouncer />
    </div>
  );
}

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [question, setQuestion] = useState('');

  const ask = (value: string) => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      void navigate('/search');
      return;
    }

    void navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setQuestion('');
  };

  return (
    <header className={styles['header']}>
      <Container size="xl">
        <div className={styles['headerInner']}>
          <AppLink to="/courses" className={styles['brand']} variant="subtle">
            HCLTech catalog
          </AppLink>

          <nav className={styles['nav']} aria-label="Main">
            {NAV.map((item) => (
              <NavItem key={item.to} to={item.to} label={item.label} />
            ))}
          </nav>

          <div className={styles['headerSearch']}>
            <SearchInput
              value={question}
              onValueChange={setQuestion}
              onSearch={ask}
              size="sm"
              placeholder="Ask about the catalog…"
              aria-label="Ask a question about the catalog"
            />
          </div>

          <div className={styles['headerActions']}>
            <ThemeToggle />
            <UserMenu key={location.pathname === '/login' ? 'login' : 'app'} />
          </div>
        </div>
      </Container>
    </header>
  );
}

/**
 * `NavLink` rather than `AppLink`, for `aria-current="page"`.
 *
 * `end` is deliberately off: `/courses` should stay marked while the reader is on a course detail
 * page, because that is still the part of the product they are in.
 */
function NavItem({ to, label }: { to: string; label: string }): ReactNode {
  return (
    <NavLink to={to} className={styles['navLink'] ?? ''}>
      {label}
    </NavLink>
  );
}

function RouteFallback() {
  return (
    <Stack align="center" gap="6" style={{ paddingBlock: 'var(--ex-space-12)' }}>
      <Spinner size="lg" label="Loading" />
    </Stack>
  );
}
